// ============================================================================
// FF-CART.JS - Refactored Cart Management System
// ============================================================================
// Modules:
// 1. CartConfig      - Section configuration
// 2. FfEvents        - Event system
// 3. CartAPI         - Network operations
// 4. CartState       - Local state + line controllers
// 5. CartUI          - DOM manipulation & rendering
// 6. EventHandlers   - UI event attachment
// 7. FfCartService   - Public API
// ============================================================================

// ============================================================================
// 1. CONFIGURATION
// ============================================================================
const CartConfig = (() => {
  const isDrawer = !!document.querySelector(".ff-cart-drawer");

  if (isDrawer) {
    return {
      cartApiIds: [
        {
          sectionApiId: "ff-cart-badge",
          selectors: [".ff-cart-count-number--js"],
          fullSectionSelector: ".ff-cart-count-number--js",
        },
        {
          sectionApiId: "ff-cart-drawer",
          hasCartLines: true,
          fullSectionSelector: ".ff-cart-drawer_content--js",
          selectors: [
            ".ff-cart-drawer_footer--js",
            ".ff-cart-drawer_shipping-notice--js",
            ".ff-cart-drawer_shipping-bar--js",
            ".ff-cart-count--js"
          ],
        },
      ],
    };
  }

  // Main cart page configuration - single section with nested blocks
  const config = {
    cartApiIds: [
      {
        sectionApiId: "ff-cart-badge",
        selectors: [".ff-cart-count-number--js"],
        fullSectionSelector: ".ff-cart-count-number--js",
      },
      {
        sectionApiId: "ff-main-cart",
        hasCartLines: true,
        fullSectionSelector: ".ff-main-cart--js",
        selectors: [
          ".ff-main-cart-slots",
          ".ff-main-cart__actions",
          ".ff-main-cart-footer-section--js",
        ],
      },
    ],
  };

  return config;
})();

// ============================================================================
// 2. EVENT SYSTEM
// ============================================================================
const FfEvents = (() => {
  const listeners = {};

  return {
    on(event, fn) {
      if (!listeners[event]) listeners[event] = [];
      listeners[event].push(fn);
    },
    off(event, fn) {
      if (!listeners[event]) return;
      listeners[event] = listeners[event].filter((f) => f !== fn);
    },
    emit(event, payload) {
      listeners[event]?.forEach((fn) => fn(payload));
    },
  };
})();

// ============================================================================
// 3. CART API - Network Operations
// ============================================================================
const CartAPI = (() => {
  function prepareBody(input) {
    if (input instanceof HTMLFormElement) return new FormData(input);
    if (typeof input === "string")
      return new FormData(document.querySelector(input));
    return JSON.stringify(input);
  }

  async function request(url, options = {}) {
    const headers = { ...options.headers };
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${window.Shopify.routes.root}${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      const errorMsg = error?.description || error?.message || response.status;
      throw new Error(errorMsg);
    }
    return response.json();
  }

  return {
    async add(payload) {
      const body = prepareBody(payload);
      return request("cart/add.js", { method: "POST", body });
    },

    async update(payload) {
      const body = prepareBody(payload);
      return request("cart/update.js", { method: "POST", body });
    },

    async clear() {
      return request("cart/clear.js", {
        method: "POST",
        body: JSON.stringify({}),
      });
    },

    async fetch() {
      return request("cart.js");
    },

    async fetchSections(sectionIds) {
      const url = "?sections[]=" + sectionIds.join("&sections[]=");
      return request(url);
    },
  };
})();

// ============================================================================
// 4. DISCOUNT STATE - Local Discount Code Management
// ============================================================================
const DiscountState = (() => {
  const codes = new Set();

  return {
    init() {
      const initialDiscounts = document.body.dataset.ffCartDiscounts || "";
      if (initialDiscounts) {
        initialDiscounts.split(",").forEach((code) => {
          const trimmed = code.trim();
          if (trimmed) codes.add(trimmed);
        });
      }
    },

    add(code) {
      const trimmed = code.trim();
      if (trimmed) codes.add(trimmed);
    },

    remove(code) {
      codes.delete(code.trim());
    },

    has(code) {
      return codes.has(code.trim());
    },

    clear() {
      codes.clear();
    },

    toString() {
      return Array.from(codes).join(",");
    },

    getAll() {
      return new Set(codes);
    },

    // Sync state with body dataset after successful update
    syncToBody() {
      document.body.dataset.ffCartDiscounts = this.toString();
    },
  };
})();

// ============================================================================
// 5. CART STATE - State Management + Line Controllers
// ============================================================================
const CartState = (() => {
  const lineItems = new Map();
  const optimisticLines = new Map();
  const controllers = new Map();
  let cartLineTemplate = null;

  // --- Line Controller Class ---
  class LineController {
    constructor(key) {
      this.key = key;
      this.rejectFn = () => {};
      this.timerId = null;
      this.debounceDelay = 300;
    }

    updateKey(newKey) {
      this.key = newKey;
    }

    async update(qty) {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.rejectFn("[DEBOUNCE] cancelled");
      }

      return new Promise((resolve, reject) => {
        this.rejectFn = reject;
        this.timerId = setTimeout(async () => {
          this.timerId = null;
          const lineEl = document.querySelector(
            `.ff-cart-line-${this.key.replace(":", "-")}--js`
          );
          lineEl?.classList.add("ff-cart-line--locked");

          try {
            const result = await CartAPI.update({
              updates: { [this.key]: qty },
            });

            if (result.items) {
              CartState.syncKeysFromResponse(result.items);
            }
            resolve(result);
          } catch (error) {
            reject(error);
          } finally {
            lineEl?.classList.remove("ff-cart-line--locked");
          }
        }, this.debounceDelay);
      });
    }
  }

  return {
    // --- Initialization ---
    init() {
      document.querySelectorAll(".ff-cart-line-item--js").forEach((el) => {
        const key = el.dataset.ffCartItemKey;
        if (!key) return;

        el.classList.add(`ff-cart-line-${key.replace(":", "-")}--js`);

        if (!cartLineTemplate) {
          cartLineTemplate = el.cloneNode(true);
          cartLineTemplate.classList.remove(
            `ff-cart-line-${key.replace(":", "-")}--js`
          );
          cartLineTemplate.removeAttribute("style");
          cartLineTemplate.classList.add("ff-cart-line--optimistic");
          cartLineTemplate.style.setProperty(
            "--ff-optimistic-line-height",
            `${cartLineTemplate.offsetHeight}px`
          );
          cartLineTemplate
            .querySelectorAll(".ff-cart-line-unrequired-field--js")
            .forEach((el) => el.remove());
        }

        let properties = {};
        try {
          properties = JSON.parse(el.dataset.ffProperties || "{}");
        } catch (e) {
          properties = {};
        }

        lineItems.set(key, {
          productId: el.dataset.ffItemId || "",
          variantId: el.dataset.ffVariantId || "",
          sellingPlanId: el.dataset.ffSellingPlanId || "",
          properties,
          quantity: parseInt(el.querySelector(".ff-cart-qty--js")?.value) || 1,
          unitPrice: {
            final: parseInt(el.dataset.ffUnitPriceFinal) || 0,
            original: parseInt(el.dataset.ffUnitPriceOriginal) || 0,
            compareAt: parseInt(el.dataset.ffUnitCompareAtPrice) || 0,
          },
          linePrice: {
            final: parseInt(el.dataset.ffLinePriceFinal) || 0,
            original: parseInt(el.dataset.ffLinePriceOriginal) || 0,
          },
        });
      });
    },

    clear() {
      lineItems.clear();
      optimisticLines.clear();
    },

    getTemplate() {
      return cartLineTemplate;
    },

    // --- Getters ---
    getProductIds() {
      const productIds = new Set();
      lineItems.forEach((data) => {
        if (data.productId) productIds.add(data.productId.toString());
      });
      optimisticLines.forEach((data) => {
        if (data.productId) productIds.add(data.productId.toString());
      });
      return productIds;
    },

    getLineData(key) {
      return optimisticLines.get(key) || lineItems.get(key);
    },

    getTotalQuantityForVariant(variantId, excludeKey = null) {
      let total = 0;
      const targetVariantId = String(variantId);

      lineItems.forEach((data, key) => {
        if (key !== excludeKey && data.variantId === targetVariantId) {
          total += data.quantity || 0;
        }
      });

      optimisticLines.forEach((data, key) => {
        if (key !== excludeKey && data.variantId === targetVariantId) {
          total += data.quantity || 0;
        }
      });

      return total;
    },

    isEmpty() {
      return lineItems.size === 0 && optimisticLines.size === 0;
    },

    // --- Line Management ---
    removeLine(key) {
      if (lineItems.has(key)) {
        lineItems.delete(key);
      } else {
        optimisticLines.delete(key);
      }
      controllers.delete(key);

      if (this.isEmpty()) {
        document.body.classList.add("ff-cart--clearing");
      }
    },

    addOptimistic(optimisticId, data) {
      optimisticLines.set(optimisticId, data);
    },

    convertOptimisticToReal(optimisticId, lineKey, lineData) {
      optimisticLines.delete(optimisticId);
      lineItems.set(lineKey, lineData);

      const optimisticEl = document.querySelector(
        `.ff-optimistic-${optimisticId}--js`
      );
      if (optimisticEl) {
        optimisticEl.classList.remove(`ff-optimistic-${optimisticId}--js`);
        optimisticEl.classList.add(
          `ff-cart-line-${lineKey.replace(":", "-")}--js`
        );
        optimisticEl.classList.remove("ff-cart-line--optimistic");
        optimisticEl.dataset.ffCartItemKey = lineKey;

        optimisticEl
          .querySelectorAll("[data-ff-cart-item-key]")
          .forEach((el) => {
            el.dataset.ffCartItemKey = lineKey;
          });

        const priceEl = optimisticEl.querySelector(".ff-cart-line-price--js");
        if (priceEl) {
          const formattedFinal = Shopify.formatMoney(
            lineData.linePrice.final,
            Shopify.money_format
          );
          const priceValueEl = priceEl.querySelector(".ff-cart-line-price-value--js");
          if (priceValueEl) {
            priceValueEl.textContent = formattedFinal;
          } else {
            priceEl.innerHTML = `<span class="ff-cart-line-price-value--js">${formattedFinal}</span>`;
          }
        }

        // Update PVP element for optimistic line
        const pvpEl = optimisticEl.querySelector(".ff-cart-line-pvp--js");
        if (pvpEl) {
          if (lineData.linePrice.original > lineData.linePrice.final) {
            const pvpValueEl = pvpEl.querySelector(".ff-cart-line-pvp-value--js");
            if (pvpValueEl) {
              pvpValueEl.textContent = Shopify.formatMoney(lineData.linePrice.original, Shopify.money_format).replace(Shopify.currency.symbol, '').trim();
            }
            pvpEl.classList.remove("ff-d-none");
          } else {
            pvpEl.classList.add("ff-d-none");
          }
        }

        EventHandlers.attachAll(optimisticEl);
      }
    },

    generateOptimisticId(variantId, sellingPlanId = "") {
      return `${variantId}_${sellingPlanId}`;
    },

    // --- Key Synchronization ---
    updateKey(oldKey, newKey) {
      let updated = false;

      if (lineItems.has(oldKey)) {
        const data = lineItems.get(oldKey);
        lineItems.delete(oldKey);
        lineItems.set(newKey, data);
        this._updateKeyInDOM(oldKey, newKey, "ff-cart-line");
        updated = true;
      }

      if (!updated && optimisticLines.has(oldKey)) {
        const data = optimisticLines.get(oldKey);
        optimisticLines.delete(oldKey);
        optimisticLines.set(newKey, data);
        this._updateKeyInDOM(oldKey, newKey, "ff-optimistic");
        updated = true;
      }

      if (updated && controllers.has(oldKey)) {
        const controller = controllers.get(oldKey);
        controller.updateKey(newKey);
        controllers.delete(oldKey);
        controllers.set(newKey, controller);
      }

      return updated;
    },

    _updateKeyInDOM(oldKey, newKey, classPrefix) {
      const oldClass = `${classPrefix}-${oldKey.replace(":", "-")}--js`;
      const newClass = `${classPrefix}-${newKey.replace(":", "-")}--js`;

      const el = document.querySelector(`.${oldClass}`);
      if (el) {
        el.classList.remove(oldClass);
        el.classList.add(newClass);
        el.dataset.ffCartItemKey = newKey;

        el.querySelectorAll("[data-ff-cart-item-key]").forEach((child) => {
          child.dataset.ffCartItemKey = newKey;
        });
      }
    },

    syncKeysFromResponse(items) {
      items.forEach((item) => {
        const variantId = String(item.variant_id);
        const sellingPlanId = String(
          item.selling_plan_allocation?.selling_plan?.id || ""
        );
        const properties = item.properties || {};
        const newKey = item.key;

        const found = this.findKeyByIdentifiers(
          variantId,
          sellingPlanId,
          properties
        );
        if (found && found.key !== newKey) {
          this.updateKey(found.key, newKey);
        }
      });
    },

    findKeyByIdentifiers(variantId, sellingPlanId, properties) {
      const match = (data) => {
        if (data.variantId !== String(variantId)) return false;
        if ((data.sellingPlanId || "") !== String(sellingPlanId || ""))
          return false;

        const dataProps = data.properties || {};
        const targetProps = properties || {};
        const dataKeys = Object.keys(dataProps);
        const targetKeys = Object.keys(targetProps);

        if (dataKeys.length !== targetKeys.length) return false;
        return dataKeys.every((key) => dataProps[key] === targetProps[key]);
      };

      for (const [key, data] of lineItems) {
        if (match(data)) return { key, isOptimistic: false };
      }
      for (const [key, data] of optimisticLines) {
        if (match(data)) return { key, isOptimistic: true };
      }
      return null;
    },

    // --- Line Controllers ---
    getController(key) {
      if (!controllers.has(key)) {
        controllers.set(key, new LineController(key));
      }
      return controllers.get(key);
    },

    // --- Price Updates ---
    updateLinePriceOnQtyChange(lineKey, newQty, cartLineEl) {
      const lineData = this.getLineData(lineKey);
      if (!lineData?.unitPrice) return;

      const unitPriceFinal = lineData.unitPrice.final || 0;
      const unitPriceOriginal = lineData.unitPrice.original || 0;
      const unitCompareAt = lineData.unitPrice.compareAt || 0;

      const newLinePriceFinal = unitPriceFinal * newQty;
      const newLinePriceOriginal = unitPriceOriginal * newQty;

      // Calculate compare_at discount (product-level)
      let compareAtDiscount = 0;
      if (unitCompareAt > unitPriceOriginal) {
        compareAtDiscount = (unitCompareAt - unitPriceOriginal) * newQty;
      }

      // Calculate line-level discount (cart discounts)
      const lineLevelDiscount = newLinePriceOriginal - newLinePriceFinal;

      // Total discount = compare_at + line_level
      const totalDiscount = compareAtDiscount + lineLevelDiscount;

      // Strike price: use compare_at if available, else original
      const strikePrice = compareAtDiscount > 0
        ? unitCompareAt * newQty
        : newLinePriceOriginal;

      lineData.quantity = newQty;
      lineData.linePrice = {
        final: newLinePriceFinal,
        original: newLinePriceOriginal,
      };

      // Update price element (final price only, no strike)
      const priceEl = cartLineEl?.querySelector(".ff-cart-line-price--js");
      if (priceEl && unitPriceFinal) {
        const formattedPrice = Shopify.formatMoney(
          newLinePriceFinal,
          Shopify.money_format
        );
        const priceValueEl = priceEl.querySelector(".ff-cart-line-price-value--js");
        if (priceValueEl) {
          priceValueEl.textContent = formattedPrice;
        } else {
          priceEl.innerHTML = `<span class="ff-cart-line-price-value--js">${formattedPrice}</span>`;
        }
      }

      // Update PVP element (compare at price)
      const pvpEl = cartLineEl?.querySelector(".ff-cart-line-pvp--js");
      if (pvpEl) {
        if (totalDiscount > 0) {
          const pvpValueEl = pvpEl.querySelector(".ff-cart-line-pvp-value--js");
          if (pvpValueEl) {
            pvpValueEl.textContent = Shopify.formatMoney(strikePrice, Shopify.money_format).replace(Shopify.currency.symbol, '').trim();
          }
          pvpEl.classList.remove("ff-d-none");
        } else {
          pvpEl.classList.add("ff-d-none");
        }
      }

      // Update discount badge
      const discountEl = cartLineEl?.querySelector(".ff-cart-line-discount-value--js");
      if (discountEl) {
        if (totalDiscount > 0) {
          const formattedDiscount = Shopify.formatMoney(
            totalDiscount,
            Shopify.money_format
          );
          discountEl.textContent = `-${formattedDiscount}`;
          discountEl.closest(".ff-cart-drawer_item-discount")?.classList.remove("ff-d-none");
        } else {
          discountEl.closest(".ff-cart-drawer_item-discount")?.classList.add("ff-d-none");
        }
      }
    },
  };
})();

// ============================================================================
// 6. CART UI - DOM Manipulation & Rendering
// ============================================================================
const CartUI = (() => {
  let toastErrorTimeout = null;

  function getMaxQtyMessage(quantity) {
    const template =
      document.body.dataset.ffMaxQtyMessage ||
      "Only {{ quantity }} units available for this product";
    return template.replace("{{ quantity }}", quantity);
  }

  return {
    // --- Section Refresh (consolidated from 3 methods) ---
    async refreshSections({ mode = "sync", clear = false } = {}) {
      if (clear) {
        document.body.classList.add("ff-cart--clearing");
        await CartAPI.clear();
      }

      const sectionIds = CartConfig.cartApiIds.map((s) => s.sectionApiId);
      const sections = await CartAPI.fetchSections(sectionIds);

      const useFullSelector = mode === "full" || clear;

      CartConfig.cartApiIds.forEach(
        ({ sectionApiId, selectors, fullSectionSelector }) => {
          const html = sections?.[sectionApiId];
          if (!html) return;

          const temp = document.createElement("div");
          temp.innerHTML = html;

          const targetSelectors = useFullSelector
            ? [fullSectionSelector]
            : selectors;

          targetSelectors.forEach((selector) => {
            if (!selector) return;
            const currentEl = document.querySelector(selector);
            const newEl = temp.querySelector(selector);
            if (!currentEl || !newEl) return;

            // Save preserved images before DOM replacement
            const preservedImages = {};
            currentEl.querySelectorAll("[data-ff-preserve-image]").forEach((el) => {
              const id = el.dataset.ffPreserveImage;
              if (!preservedImages[id]) preservedImages[id] = el.innerHTML;
            });

            currentEl.innerHTML = newEl.innerHTML;

            // Restore preserved images after DOM replacement
            if (Object.keys(preservedImages).length > 0) {
              currentEl.querySelectorAll("[data-ff-preserve-image]").forEach((el) => {
                const id = el.dataset.ffPreserveImage;
                if (preservedImages[id]) el.innerHTML = preservedImages[id];
              });
            }

            currentEl.querySelectorAll("script").forEach((script) => {
              const newScript = document.createElement("script");
              newScript.innerHTML = script.innerHTML;
              script.replaceWith(newScript);
            });
            EventHandlers.attachAll(currentEl);
          });
        }
      );

      // Reinit drawer if present
      const cartDrawer = document.querySelector(".ff-cart-drawer");
      if (cartDrawer?.ffInit) cartDrawer.ffInit(true);

      // Reset state if needed
      if (clear || mode === "full") {
        CartState.clear();
        if (mode === "full") CartState.init();
      }

      document.body.classList.remove("ff-cart--updating", "ff-cart--clearing");
    },

    cleanupRemovedLines() {
      document
        .querySelectorAll(".ff-cart-line--removed-js")
        .forEach((el) => {
          // Main cart slots are replaced by section refresh, not removed
          if (el.closest(".ff-main-cart-slots")) return;
          el.remove();
        });
    },

    // --- Optimistic Line ---
    addOptimisticLine({
      url,
      title,
      price,
      image,
      variantId,
      sellingPlanId = "",
      quantity = 1,
      maxQty = null,
      optionsElReplace = "",
      productId = "",
      properties = {},
    }) {
      const template = CartState.getTemplate();
      if (!template) return { element: null, optimisticId: null };

      const lastCartLine = [
        ...document.querySelectorAll(".ff-cart-line-item--js"),
      ].pop();
      if (!lastCartLine) return { element: null, optimisticId: null };

      const optimisticId = CartState.generateOptimisticId(
        variantId,
        sellingPlanId
      );

      CartState.addOptimistic(optimisticId, {
        title,
        price,
        image,
        quantity,
        productId,
        variantId: String(variantId),
        sellingPlanId: String(sellingPlanId || ""),
        properties,
      });

      const optimisticLine = template.cloneNode(true);
      optimisticLine.classList.add(`ff-optimistic-${optimisticId}--js`);

      if (maxQty) {
        optimisticLine.dataset.ffMaxQty = maxQty;
      } else {
        delete optimisticLine.dataset.ffMaxQty;
      }

      const img = optimisticLine.querySelector(".ff-cart-line-image--js");
      if (img && image) {
        img.src = image;
        img.alt = title || "";
        img.srcset = "";
      }

      const urlEl = optimisticLine.querySelector("a");
      if (urlEl) urlEl.href = url || "";

      const titleEl = optimisticLine.querySelector(".ff-cart-line-title--js");
      if (titleEl) titleEl.textContent = title || "";

      const priceEl = optimisticLine.querySelector(".ff-cart-line-price--js");
      if (priceEl) priceEl.textContent = price || "";

      const qtyInput = optimisticLine.querySelector(".ff-cart-line-qty--js");
      if (qtyInput) qtyInput.value = quantity;

      const optionsEl = optimisticLine.querySelector(
        ".ff-cart-item-options--js"
      );
      if (optionsEl && optionsElReplace) {
        optionsEl.replaceWith(optionsElReplace);
      } else {
        optionsEl?.remove();
      }

      lastCartLine.after(optimisticLine);
      lastCartLine.parentElement.scrollTop =
        lastCartLine.parentElement.scrollHeight;

      return { element: optimisticLine, optimisticId };
    },

    // --- Remove Cart Line with Animation ---
    removeCartLine(cartLineEl) {
      if (!cartLineEl) return;

      const lineKey = cartLineEl.dataset.ffCartItemKey;
      if (lineKey) CartState.removeLine(lineKey);

      // Main cart slots: fade only (no collapse), section refresh replaces with empty placeholder
      if (cartLineEl.closest(".ff-main-cart-slots")) {
        cartLineEl.style.opacity = "0.3";
        cartLineEl.style.transition = "opacity 0.2s ease";
        cartLineEl.style.pointerEvents = "none";
        cartLineEl.classList.add("ff-cart-line--removed-js");
        return;
      }

      const computedStyle = window.getComputedStyle(cartLineEl);
      const isDisplayContents = computedStyle.display === "contents";

      if (isDisplayContents) {
        const children = Array.from(cartLineEl.children);
        const firstChild = children[0];
        if (!firstChild) return;

        const height = firstChild.offsetHeight;
        children.forEach((child) => {
          child.style.marginTop = `-${height}px`;
          child.style.opacity = "0.3";
          child.style.transition = "margin-top 0.3s ease, opacity 0.3s ease";
          child.style.zIndex = "0";
          child.classList.add("ff-cart-line--removed-js");
        });
      } else {
        const height = cartLineEl.offsetHeight;
        cartLineEl.style.marginTop = `-${height}px`;
        cartLineEl.style.opacity = "0.3";
        cartLineEl.style.transition = "margin-top 0.3s ease, opacity 0.3s ease";
        cartLineEl.style.zIndex = "0";
        cartLineEl.classList.add("ff-cart-line--removed-js");
      }
    },

    // --- Toast Messages ---
    showToastError(message) {
      const toast = document.getElementById("ff-cart-error-toast");
      if (!toast) return;

      toast.textContent = message;
      requestAnimationFrame(() =>
        toast.classList.add("ff-cart-toast--visible")
      );

      if (toastErrorTimeout) clearTimeout(toastErrorTimeout);
      toastErrorTimeout = setTimeout(() => {
        toast.classList.remove("ff-cart-toast--visible");
      }, 3000);
    },

    getMaxQtyMessage,
  };
})();

// ============================================================================
// 6b. QUANTITY BUTTON STATE - Update disabled based on current value
// ============================================================================
function updateQtyButtonStates(input) {
  const qtySelector = input.closest(".ff-qty-selector");
  if (!qtySelector) return;

  const qty = parseInt(input.value) || 0;
  const minQty = parseInt(input.min) || 0;
  const cartLine = input.closest(".ff-cart-line-item--js");
  const maxQty = cartLine?.dataset.ffMaxQty;

  const decreaseBtn = qtySelector.querySelector(".ff-cart-drawer_qty-decrease--js");
  const increaseBtn = qtySelector.querySelector(".ff-cart-drawer_qty-increase--js");

  if (decreaseBtn) decreaseBtn.disabled = qty <= minQty;
  if (increaseBtn) increaseBtn.disabled = !!(maxQty && qty >= parseInt(maxQty));
}

// ============================================================================
// 7. EVENT HANDLERS - UI Event Attachment
// ============================================================================
const EventHandlers = (() => {
  return {
    attachAll(container) {
      this.attachAddToCart(container);
      this.attachRemove(container);
      this.attachQuantityChange(container);
      this.attachQuantityButtons(container);
      this.attachDiscountForm(container);
    },

    attachAddToCart(container) {
      container.querySelectorAll(".ff-cart-add--js").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          e.preventDefault();
          e.stopPropagation();

          const form = btn.closest("form");
          const formData = new FormData(form);
          const variantId = formData.get("id") || btn.dataset.ffVariantId;
          const sellingPlanId = formData.get("selling_plan") || "";
          const quantity = parseInt(formData.get("quantity")) || 1;
          const maxQty = btn.dataset.ffMaxQty || null;

          if (maxQty && variantId) {
            const maxQtyInt = parseInt(maxQty);
            const currentTotalQty =
              CartState.getTotalQuantityForVariant(variantId);

            if (currentTotalQty + quantity > maxQtyInt) {
              CartUI.showToastError(CartUI.getMaxQtyMessage(maxQty));
              return;
            }
          }

          btn.disabled = true;

          const { optimisticId } = CartUI.addOptimisticLine({
            url: btn.dataset.ffProductUrl,
            title: btn.dataset.ffProductTitle,
            price: btn.dataset.ffProductPrice,
            image: btn.dataset.ffProductImage,
            variantId,
            sellingPlanId,
            quantity,
            maxQty,
            optionsElReplace: form.querySelector(".ff-cart-item-options--js"),
          });

          await FfCartService.add(form, { optimisticId });
        });
      });
    },

    attachRemove(container) {
      container.querySelectorAll(".ff-cart-remove--js").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          e.preventDefault();
          const key = btn.dataset.ffCartItemKey;
          if (!key) return;

          const cartLine = btn.closest(".ff-cart-line-item--js");
          CartUI.removeCartLine(cartLine);

          await FfCartService.updateLine(key, 0);
        });
      });
    },

    attachQuantityChange(container) {
      container.querySelectorAll(".ff-cart-qty--js").forEach((input) => {
        if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
          input.style.pointerEvents = "none";
        }

        input.addEventListener("change", async (e) => {
          const key = input.dataset.ffCartItemKey;
          const qty = parseInt(e.target.value);

          const cartLine = input.closest(".ff-cart-line-item--js");
          const maxQty = cartLine?.dataset.ffMaxQty;
          const variantId = cartLine?.dataset.ffVariantId;

          if (qty === 0) {
            CartUI.removeCartLine(cartLine);
          }

          if (maxQty && variantId) {
            const maxQtyInt = parseInt(maxQty);
            const otherLinesQty = CartState.getTotalQuantityForVariant(
              variantId,
              key
            );
            const totalQty = otherLinesQty + qty;

            if (totalQty > maxQtyInt) {
              const allowedQty = Math.max(0, maxQtyInt - otherLinesQty);
              input.value = allowedQty;
              updateQtyButtonStates(input);
              CartUI.showToastError(CartUI.getMaxQtyMessage(maxQty));

              if (allowedQty === 0) {
                CartUI.removeCartLine(cartLine);
              }
              return;
            }
          }

          updateQtyButtonStates(input);
          CartState.updateLinePriceOnQtyChange(key, qty, cartLine);
          await FfCartService.updateLine(key, qty);
        });
      });
    },

    attachQuantityButtons(container) {
      // Decrease
      container
        .querySelectorAll(".ff-cart-drawer_qty-decrease--js")
        .forEach((btn) => {
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            const key = btn.dataset.ffCartItemKey;
            const input = container.querySelector(
              `input[data-ff-cart-item-key="${key}"]`
            );

            const currentQty = parseInt(input.value);
            input.value = Math.max(0, currentQty - 1);
            input.dispatchEvent(new Event("change"));
          });
        });

      // Increase
      container
        .querySelectorAll(".ff-cart-drawer_qty-increase--js")
        .forEach((btn) => {
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            const key = btn.dataset.ffCartItemKey;
            const input = container.querySelector(
              `input[data-ff-cart-item-key="${key}"]`
            );

            const currentQty = parseInt(input.value);
            input.value = currentQty + 1;
            input.dispatchEvent(new Event("change"));
          });
        });
    },

    attachDiscountForm(container) {
      container
        .querySelectorAll(".ff-discount-form_form--js")
        .forEach((form) => {
          const discountCodeContainer = form.closest(
            ".ff-discount-form_container--js"
          );

          if (discountCodeContainer) {
            const trigger = discountCodeContainer.querySelector(
              ".ff-discount-form_trigger--js"
            );
            trigger?.addEventListener("click", (e) => {
              e.preventDefault();
              discountCodeContainer.style.setProperty(
                "--ff-discount-form-height",
                form.offsetHeight + "px"
              );
              discountCodeContainer.classList.toggle(
                "ff-discount-form--open-js"
              );
            });
          }

          // Add discount code
          form.addEventListener("submit", async (e) => {
            e.preventDefault();
            const formData = new FormData(form);
            const discountCode = formData.get("discount")?.trim();
            if (!discountCode) return;

            // Add to local state
            DiscountState.add(discountCode);

            // Clear input
            const input = form.querySelector(".ff-discount-form_input--js");
            if (input) input.value = "";

            // Send all discount codes to cart
            await FfCartService.update(
              { discount: DiscountState.toString() },
              { startFresh: true }
            );
            DiscountState.syncToBody();
          });
        });

      // Remove discount code
      container
        .querySelectorAll(".ff-discount-form_applied-remove--js")
        .forEach((btn) => {
          btn.addEventListener("click", async (e) => {
            e.preventDefault();
            const discountCode = btn.dataset.ffDiscount;
            if (!discountCode) return;

            // Remove from local state
            DiscountState.remove(discountCode);

            // Send remaining discount codes to cart
            await FfCartService.update(
              { discount: DiscountState.toString() },
              { startFresh: true }
            );
            DiscountState.syncToBody();
          });
        });
    },
  };
})();

// ============================================================================
// 8. ORCHESTRATION - Operation Flow Control
// ============================================================================
let stackedFetches = 0;

/**
 * Synchronize cart state and DOM with server data
 * Fetches current cart, updates line prices in DOM, syncs CartState, then refreshes sections
 */
async function synchronize() {
  // Fetch current cart state from server
  const cart = await CartAPI.fetch();

  // Sync keys first (in case they changed due to discounts)
  if (cart.items) {
    CartState.syncKeysFromResponse(cart.items);
  }

  // Sync prices in CartState and DOM for each line item
  cart.items?.forEach((item) => {
    const lineKey = item.key;
    const lineData = CartState.getLineData(lineKey);

    if (lineData) {
      // Preserve compareAt from initial state (not in cart API response)
      const unitCompareAt = lineData.unitPrice?.compareAt || 0;

      // Update CartState with server prices
      lineData.quantity = item.quantity;
      lineData.unitPrice = {
        final: item.final_price,
        original: item.original_price,
        compareAt: unitCompareAt,
      };
      lineData.linePrice = {
        final: item.final_line_price,
        original: item.original_line_price,
      };

      // Calculate discounts
      let compareAtDiscount = 0;
      if (unitCompareAt > item.original_price) {
        compareAtDiscount = (unitCompareAt - item.original_price) * item.quantity;
      }
      const lineLevelDiscount = item.line_level_total_discount || 0;
      const totalDiscount = compareAtDiscount + lineLevelDiscount;

      // Strike price: use compare_at if available, else original
      const strikePrice = compareAtDiscount > 0
        ? unitCompareAt * item.quantity
        : item.original_line_price;

      // Update DOM price for this line (final price only, no strike)
      const lineEl = document.querySelector(
        `.ff-cart-line-${lineKey.replace(":", "-")}--js`
      );
      const priceEl = lineEl?.querySelector(".ff-cart-line-price--js");

      if (priceEl) {
        const formattedPrice = Shopify.formatMoney(
          item.final_line_price,
          Shopify.money_format
        );
        const priceValueEl = priceEl.querySelector(".ff-cart-line-price-value--js");
        if (priceValueEl) {
          priceValueEl.textContent = formattedPrice;
        } else {
          priceEl.innerHTML = `<span class="ff-cart-line-price-value--js">${formattedPrice}</span>`;
        }
      }

      // Update PVP element (compare at price)
      const pvpEl = lineEl?.querySelector(".ff-cart-line-pvp--js");
      if (pvpEl) {
        if (totalDiscount > 0) {
          const pvpValueEl = pvpEl.querySelector(".ff-cart-line-pvp-value--js");
          if (pvpValueEl) {
            pvpValueEl.textContent = Shopify.formatMoney(strikePrice, Shopify.money_format).replace(Shopify.currency.symbol, '').trim();
          }
          pvpEl.classList.remove("ff-d-none");
        } else {
          pvpEl.classList.add("ff-d-none");
        }
      }

      // Update discount badge
      const discountEl = lineEl?.querySelector(".ff-cart-line-discount-value--js");
      if (discountEl) {
        if (totalDiscount > 0) {
          const formattedDiscount = Shopify.formatMoney(
            totalDiscount,
            Shopify.money_format
          );
          discountEl.textContent = `-${formattedDiscount}`;
          discountEl.closest(".ff-cart-drawer_item-discount")?.classList.remove("ff-d-none");
        } else {
          discountEl.closest(".ff-cart-drawer_item-discount")?.classList.add("ff-d-none");
        }
      }
    }
  });

  // Price discrepancies between optimistic prediction and server are expected
  // when the user makes rapid quantity changes (debounced requests overlap).
  // The server-authoritative prices are already applied above, so no toast needed.

  // Refresh partial sections (footer, summary, etc.)
  await CartUI.refreshSections({ mode: "sync" });
}

async function perform(fn, operation, context = {}) {
  stackedFetches++;

  try {
    FfEvents.emit("cart:loading", { operation, ...context });
    const result = await fn();
    FfEvents.emit("cart:updated", { cart: result, operation, ...context });
  } catch (error) {
    if (error.toString().includes("[DEBOUNCE]")) {
      stackedFetches--;
      return;
    }
    FfEvents.emit("cart:error", { error, operation, ...context });
    stackedFetches--;
    return;
  }

  stackedFetches--;

  if (stackedFetches === 0) {
    CartUI.cleanupRemovedLines();

    if (context.startFresh) {
      await CartUI.refreshSections({ mode: "full" });
    } else if (CartState.isEmpty()) {
      await CartUI.refreshSections({ mode: "full", clear: true });
    } else {
      await synchronize();
    }
  }
}

// --- Debounce Utility ---
const ffDebounce = (fn, delay = 300) => {
  let timerId;
  let rejectFn;

  return async function (...args) {
    if (timerId) {
      clearTimeout(timerId);
      rejectFn?.("[DEBOUNCE] cancelled");
    }

    return new Promise((resolve, reject) => {
      rejectFn = reject;
      timerId = setTimeout(async () => {
        timerId = null;
        try {
          resolve(await fn(...args));
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
};

const debouncedClear = ffDebounce(() => CartAPI.clear(), 300);

// ============================================================================
// 9. PUBLIC API - FfCartService
// ============================================================================
const FfCartService = {
  async add(payload, context = {}) {
    return perform(
      async () => {
        const response = await CartAPI.add(payload);
        const addedItems = response.items || [response];

        if (context.optimisticId && addedItems.length > 0) {
          const addedItem = addedItems[0];
          CartState.convertOptimisticToReal(
            context.optimisticId,
            addedItem.key,
            {
              properties: addedItem.properties,
              sellingPlanId:
                addedItem.selling_plan_allocation?.selling_plan?.id || "",
              variantId: addedItem.variant_id,
              productId: addedItem.product_id,
              quantity: addedItem.quantity,
              unitPrice: {
                final: addedItem.final_price,
                original: addedItem.original_price,
              },
              linePrice: {
                final: addedItem.final_line_price,
                original: addedItem.original_line_price,
              },
            }
          );
        }

        return response;
      },
      "add",
      context
    );
  },

  async updateLine(key, qty, context = {}) {
    const controller = CartState.getController(key);
    return perform(
      async () => {
        const result = await controller.update(qty);
        return result;
      },
      "update",
      { ...context, lineKey: key }
    );
  },

  async update(payload, context = {}) {
    return perform(
      async () => {
        const result = await CartAPI.update(payload);
        if (result.items) {
          CartState.syncKeysFromResponse(result.items);
        }
        return result;
      },
      "update",
      context
    );
  },

  async clear(context = {}) {
    return perform(() => debouncedClear(), "clear", context);
  },

  fetch: () => CartAPI.fetch(),
  synchronize,
};

// ============================================================================
// 10. INITIALIZATION
// ============================================================================
function init() {
  // Initialize state
  CartState.init();
  DiscountState.init();

  // Attach events to existing cart elements
  CartConfig.cartApiIds.forEach(({ fullSectionSelector }) => {
    const el = document.querySelector(fullSectionSelector);
    if (el) EventHandlers.attachAll(el);
  });

  // Setup global event listeners
  FfEvents.on("cart:loading", ({ source }) => {
    document.body.classList.add("ff-cart--updating");
    source?.ffCartLoading?.();
  });

  FfEvents.on("cart:updated", ({ cart, source }) => {
    source?.ffCartUpdated?.(cart);
  });

  FfEvents.on("cart:error", ({ error, source }) => {
    CartUI.refreshSections({ mode: "full" });
    CartUI.showToastError(error);
    source?.ffCartError?.(error);
  });
}

// Run initialization
init();
